import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useSeoMeta, SEO_BASE_URL } from "../lib/useSeoMeta";
import { useAuth } from "../lib/auth";
import ShareButton from "../components/ShareButton";
import PlayStoreBadge from "../components/PlayStoreBadge";
import QuickLinksPanel from "../components/QuickLinksPanel";
import AdukooAdSlot from "../components/AdukooAdSlot";
import {
    ArrowRight, ShieldCheck, Tag, Zap, Users, Eye, ListChecks, Sparkles, Heart,
    User as UserIcon, BadgeCheck, Briefcase, DollarSign, Globe2, Infinity as InfinityIcon,
    MapPin, MessageSquare, Package, Palette, Plus, Store, Wallet, PartyPopper,
} from "lucide-react";

const LI = {
    ArrowRight, ShieldCheck, Tag, Zap, Users, Eye, ListChecks, Sparkles, Heart,
    BadgeCheck, Briefcase, DollarSign, Globe2, Infinity: InfinityIcon, MapPin,
    MessageSquare, Package, Palette, Plus, Store, Wallet,
};

// v79.18 — HERO_IMG restored (v79.17 removal reverted per founder's
// "come back to previous normal" request).
const HERO_IMG = "/marketing-assets/adukoo-yt-banner-features.png";

function CategoryIcon({ name, className }) {
    const Icon = LI[name] || LI.Tag;
    return <Icon className={className} />;
}

export default function Home() {
    const { user, loading: authLoading } = useAuth();
    const [categories, setCategories] = useState([]);

    // Skip SEO meta on /c/{country} country-landing pages — CountryLanding
    // owns those tags.
    const isCountryPath = typeof window !== "undefined" && /^\/c\//.test(window.location.pathname);
    useSeoMeta(isCountryPath ? null : {
        title: "Adukoo — Worldwide marketplace | Buy, Sell & Hire",
        description: "The worldwide ads-posting & brand-building platform. Post an Ad, open a branded Digital Store, or hire — from any of 180+ countries. Adukoo turns the invisible into visible for individuals, small shops, corner businesses, brands and enterprises.",
        canonical: `${SEO_BASE_URL}/`,
        type: "website",
        jsonLd: {
            "@context": "https://schema.org",
            "@graph": [
                {
                    "@type": "Organization",
                    "@id": `${SEO_BASE_URL}/#org`,
                    "name": "Adukoo",
                    "url": `${SEO_BASE_URL}/`,
                    "logo": `${SEO_BASE_URL}/logo512.png`,
                    "areaServed": "Worldwide",
                    "description": "Worldwide marketplace. List Ads, open a branded Digital Store, and reach buyers across 180+ countries.",
                    "sameAs": [],
                },
                {
                    "@type": "WebSite",
                    "@id": `${SEO_BASE_URL}/#website`,
                    "url": `${SEO_BASE_URL}/`,
                    "name": "Adukoo",
                    "publisher": { "@id": `${SEO_BASE_URL}/#org` },
                    "inLanguage": "en",
                    "potentialAction": {
                        "@type": "SearchAction",
                        "target": `${SEO_BASE_URL}/browse?q={search_term_string}`,
                        "query-input": "required name=search_term_string",
                    },
                },
            ],
        },
    });

    // v79.195 — Home has been stripped to the essentials: Hero + Quick Links
    // + Category chips. Category rails / Digital Store rail / Sponsored Ads /
    // AdSense have all moved to Browse (contextual, category-scoped) so the
    // landing page loads fast and feels uncluttered. Categories still fetched
    // to power the swipe strip.
    useEffect(() => {
        document.title = "Adukoo — Worldwide marketplace";
        api.get("/categories")
            .then((r) => setCategories(Array.isArray(r.data) ? r.data : []))
            .catch(() => { /* soft-fail — chips just hide until network returns */ });
    }, []);

    return (
        <div data-testid="home-page" className="w-full">
            {/* HERO — compact (v73): clearer B2B·B2C·C2C marketplace framing,
                three independent CTAs (Post Ad / Create Account / Open Digital
                Store), audience strip targeting everyone from individual
                shopkeepers to large enterprises. */}
            <section className="relative overflow-hidden bg-aduku-navy text-white">
                <div className="absolute inset-0 opacity-30 hidden md:block">
                    <img src={HERO_IMG} alt="" className="w-full h-full object-cover" />
                </div>
                <div className="absolute inset-0 bg-gradient-to-r from-aduku-navy via-aduku-navy/85 to-aduku-navy/40 hidden md:block" />
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-6 lg:py-8 grid lg:grid-cols-[1.3fr_1fr] gap-4 lg:gap-7 items-center">
                    <div>
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-aduku-yellow/20 border border-aduku-yellow/40 text-aduku-yellow text-[10px] font-bold tracking-wider uppercase">
                            <Globe2 className="w-3 h-3" /> Worldwide marketplace · 180+ countries
                        </span>
                        <h1 className="mt-2 sm:mt-3 font-display text-lg sm:text-3xl md:text-4xl lg:text-5xl font-black leading-[1.1] text-white">
                            Post an Ad. <span className="text-aduku-yellow">Open a store.</span> Build a brand.
                        </h1>
                        <p className="mt-2 sm:mt-3 text-white/85 text-xs sm:text-sm max-w-xl leading-snug">
                            60 seconds to publish. Free branded Digital Store. Buyers, hirers and partners across 180+ countries.
                        </p>

                        {/* Primary CTAs — 4 max */}
                        <div className="mt-4 grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:items-center sm:gap-2.5">
                            <Link
                                to={user ? "/post" : "/register?next=/post"}
                                data-testid="hero-post-ad-btn"
                                className="block w-full sm:inline-block sm:w-auto"
                            >
                                <button className="aduku-yellow-btn w-full sm:w-auto h-10 px-3 sm:px-4 rounded-full font-bold text-xs sm:text-sm inline-flex items-center justify-center gap-1.5 shadow-lg shadow-aduku-yellow/20 whitespace-nowrap">
                                    <Zap className="w-3.5 h-3.5" /> List an Ad
                                </button>
                            </Link>
                            {!user && !authLoading && (
                                <Link to="/register" data-testid="hero-create-account-btn" className="block w-full sm:inline-block sm:w-auto">
                                    <button className="w-full sm:w-auto h-10 px-3 sm:px-4 rounded-full font-bold text-xs sm:text-sm bg-white text-aduku-navy hover:bg-white/90 inline-flex items-center justify-center gap-1.5 transition-all shadow-lg whitespace-nowrap">
                                        <UserIcon className="w-3.5 h-3.5" /> Create Account
                                    </button>
                                </Link>
                            )}
                            <Link
                                to={user
                                    ? ((user.shops && user.shops.length > 0) || user.shop_slug
                                        ? "/businesses/mine"
                                        : "/digital-stores/me/edit")
                                    : "/register?next=/digital-stores/me/edit"}
                                data-testid="hero-open-store-btn"
                                className="block w-full sm:inline-block sm:w-auto"
                            >
                                <button className="w-full sm:w-auto h-10 px-3 sm:px-4 rounded-full font-bold text-xs sm:text-sm bg-white/10 hover:bg-white/20 text-white border border-white/25 hover:border-white/50 inline-flex items-center justify-center gap-1.5 transition-all whitespace-nowrap">
                                    <Store className="w-3.5 h-3.5" />
                                    {user && ((user.shops && user.shops.length > 0) || user.shop_slug)
                                        ? "Add another Store"
                                        : "Open a Digital Store"}
                                </button>
                            </Link>
                            {/* v79.223 — Founder direction: a "Browse Digital
                                Stores" CTA was missing on mobile + Android.
                                Users could only CREATE a store from here, not
                                discover the ones already listed. Routes to
                                /digital-stores (public directory). */}
                            <Link
                                to="/digital-stores"
                                data-testid="hero-browse-stores-btn"
                                className="block w-full sm:inline-block sm:w-auto"
                            >
                                <button className="w-full sm:w-auto h-10 px-3 sm:px-4 rounded-full font-bold text-xs sm:text-sm bg-white/10 hover:bg-white/20 text-white border border-white/25 hover:border-white/50 inline-flex items-center justify-center gap-1.5 transition-all whitespace-nowrap">
                                    <Store className="w-3.5 h-3.5" /> Browse Stores
                                </button>
                            </Link>
                            <Link
                                to="/funboard/gang"
                                data-testid="hero-funboard-gang-btn"
                                className="block w-full sm:inline-block sm:w-auto"
                            >
                                <button className="w-full sm:w-auto h-10 px-3 sm:px-4 rounded-full font-bold text-xs sm:text-sm bg-aduku-yellow/15 hover:bg-aduku-yellow/25 text-aduku-yellow border border-aduku-yellow/50 hover:border-aduku-yellow inline-flex items-center justify-center gap-1.5 transition-all whitespace-nowrap">
                                    <PartyPopper className="w-3.5 h-3.5" /> Adukoo Gang
                                </button>
                            </Link>
                        </div>

                        {/* Secondary — Play Store badge + Share, one row, no extra copy */}
                        <div
                            className="mt-4 flex flex-wrap items-center gap-3"
                            data-testid="hero-engagement-row"
                        >
                            <PlayStoreBadge source="hero" variant="lg" />
                            <div data-testid="hero-share-wrapper" className="inline-flex">
                                <ShareButton path="/" title="Adukoo" variant="cta" />
                            </div>
                            <Link to="/how-it-works" data-testid="hero-how-it-works" className="inline-flex items-center gap-1 text-aduku-yellow hover:underline font-bold whitespace-nowrap text-xs">
                                See how it works <ArrowRight className="w-3 h-3" />
                            </Link>
                        </div>
                    </div>

                    {/* Right rail — brand card. v79.209 — Founder direction:
                        remove the "Near you" widget from Home so buyers
                        route through category discovery instead of a soft
                        geo-nudge. Brand card sits alone with no wasted
                        below-card space. */}
                    <div className="hidden lg:flex flex-col gap-3" data-testid="hero-brand-card">
                        <div className="rounded-2xl bg-aduku-navy-800/70 border border-white/15 shadow-2xl p-5 backdrop-blur-sm">
                            <div className="text-center">
                                <div className="font-display font-black text-aduku-yellow tracking-tight leading-none text-5xl sm:text-6xl">Adukoo</div>
                                <div className="mt-2 inline-block bg-aduku-yellow text-aduku-navy text-[10px] font-extrabold tracking-[0.32em] uppercase px-3 py-1 rounded-sm">
                                    Worldwide marketplace.
                                </div>
                            </div>
                            <div className="mt-5 grid grid-cols-4 gap-2" data-testid="hero-brand-tiles">
                                {[
                                    { Icon: Tag,       label: "Ads",        to: "/browse",                                  testid: "tile-ads" },
                                    { Icon: Store,     label: "Stores",     to: "/digital-stores",                          testid: "tile-stores" },
                                    { Icon: Briefcase, label: "Talent",     to: "/browse?category=jobs",                    testid: "tile-talent" },
                                    { Icon: Sparkles,  label: "Affiliates", to: "/browse?seller_account_type=affiliate",    testid: "tile-affiliates" },
                                ].map(({ Icon, label, to, testid }) => (
                                    <Link
                                        key={label}
                                        to={to}
                                        data-testid={testid}
                                        className="group flex flex-col items-center justify-center gap-1.5 py-3 rounded-lg bg-white/5 border border-white/10 hover:bg-aduku-yellow/15 hover:border-aduku-yellow/40 transition"
                                    >
                                        <div className="w-9 h-9 rounded-full bg-aduku-yellow/15 group-hover:bg-aduku-yellow/30 flex items-center justify-center transition">
                                            <Icon className="w-4 h-4 text-aduku-yellow" />
                                        </div>
                                        <span className="text-[10px] font-extrabold tracking-[0.18em] uppercase text-white group-hover:text-aduku-yellow transition">
                                            {label}
                                        </span>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Quick Links (minimized community solutions) */}
            <QuickLinksPanel />

            {/* v79.208 — Below-hero revenue slot. Renders nothing when the
                admin has not published an ad targeting this slot. */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-3">
                <AdukooAdSlot slot="home_hero_footer" />
            </div>

            {/* CATEGORIES — full grid (all chips visible, no View All) */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-6">
                <div className="mb-3">
                    <h2 className="font-display text-xl sm:text-2xl font-black text-aduku-navy">Browse Ads by category</h2>
                    <p className="text-aduku-navy/55 text-xs">Find what you need, or post what you don&apos;t.</p>
                </div>
                <div
                    data-testid="category-grid"
                    className="grid grid-cols-3 xs:grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 gap-2 sm:gap-3"
                >
                    {categories.map((c, i) => {
                        const isYellow = i % 2 === 0;
                        return (
                            <Link
                                key={c.slug}
                                to={`/browse?category=${c.slug}`}
                                data-testid={`category-chip-${c.slug}`}
                                className={`group bg-white border rounded-2xl px-2 py-2 sm:px-3 sm:py-3 flex flex-col items-center gap-1.5 sm:gap-2 [-webkit-tap-highlight-color:transparent] ${
                                    isYellow
                                        ? "border-aduku-yellow/40 [@media(hover:hover)]:hover:border-aduku-yellow [@media(hover:hover)]:hover:-translate-y-0.5 [@media(hover:hover)]:transition-all"
                                        : "border-aduku-navy/15 [@media(hover:hover)]:hover:border-aduku-navy [@media(hover:hover)]:hover:-translate-y-0.5 [@media(hover:hover)]:transition-all"
                                }`}
                            >
                                <div className={`w-9 h-9 sm:w-12 sm:h-12 md:w-14 md:h-14 rounded-full flex items-center justify-center ${isYellow ? "bg-aduku-yellow text-aduku-navy" : "bg-aduku-navy text-aduku-yellow"}`}>
                                    <CategoryIcon name={c.icon} className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6" />
                                </div>
                                <div className="text-[11px] sm:text-[12px] md:text-[13px] font-semibold text-aduku-navy text-center font-display leading-tight line-clamp-2">{c.name}</div>
                            </Link>
                        );
                    })}
                </div>

                {/* v79.215 — Founder direction: "Remove Browse every ad
                    button on home page." Removed. Users route through
                    the 51 category chips above (or the top-bar Search)
                    to reach the Browse feed — the button was redundant
                    once every category was visible on the grid. */}
            </section>
        </div>
    );
}
